import { useEffect, useState } from "react";
import { UseFormMethods } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { CodedProperty, ensure, getById, Optional } from "sprint-common-web";

import { SuggestContentListByTitleResponseItem } from "../../../../../common/api/catalogue-api/contents-api/SuggestContentListByTitleResponse";
import { useCatalogueContentSearchApi } from "../../../../../common/api/catalogue-api/contents-api/useCatalogueContentSearchApi";
import { CLEAN_VIDEO_SUBTITLE_LANGUAGE_ID } from "../../../../../common/api/master-api/common/consts";
import {
    CloudOrderSubtitleResponse,
    GetOrderDetailsResponseItem
} from "../../../../../common/api/orders-api/order/GetOrderDetailsResponse";
import { ContentReferenceType } from "../../../../../common/types/ContentReferenceType";
import {
    OrderContentAncillaryReference,
    OrderContentMovieReference,
    OrderContentTvSeriesReference
} from "../../../../../common/types/OrderContentReference";
import { useOrdersModuleApi } from "../../../../common/useOrdersModuleApi";
import {
    OrderAncillaryVersionItem,
    OrderEpisodeVersionItem,
    OrderMovieVersionItem
} from "../../types/OrderContentVersionItem";
import { useCreateOrder } from "../provider/useCreateOrder";
import { CreateOrderFormInputs } from "./useCreateOrderForm";
import { useEncodeSpecifications } from "./useEncodeSpecifications";
import { useOrderAirlines } from "./useOrderAirlines";
import { useResolutions } from "./useResolutions";
import { useSystems } from "./useSystems";

export const useLoadOrderForm = ({ reset }: UseFormMethods<CreateOrderFormInputs>) => {
    const {
        setAirline,
        setSystem,
        setContentType,
        setMovieVersion,
        setAncillaryVersion,
        setEpisodeVersion,
        setSoundtracks,
        setEmbeddedSubtitle,
        setDynamicSubtitles,
        setEncodeSpecification,
        setTargetResolution,
        setTargetFileName,
        setContentDetails
    } = useCreateOrder();
    const [sourceOrder, setSourceOrder] = useState<GetOrderDetailsResponseItem>();
    const { data: encodeSpecifications } = useEncodeSpecifications();
    const { data: airlines } = useOrderAirlines();
    const { data: systems } = useSystems();

    const { data: resolutions } = useResolutions();

    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const sourceOrderId = urlParams.get("sourceOrderId");

    const { getOrderDetails } = useOrdersModuleApi();
    const { suggestContentListByTitle } = useCatalogueContentSearchApi();

    useEffect(() => {
        if (sourceOrderId) {
            getOrderDetails(sourceOrderId).then((res) => {
                setSourceOrder(res.order);
            });
        }
    }, [sourceOrderId]);

    useEffect(() => {
        if (sourceOrder) {
            reset({
                airlineId: sourceOrder.airline.id,
                contentVersionId: sourceOrder.content.version.id,
                contentId: getContentId(sourceOrder),
                contentSeasonId: getContentSeasonId(sourceOrder),
                contentEpisodeId: getContentEpisodeId(sourceOrder),
                dueDate: new Date(sourceOrder.deliveryInfo.dueDate),
                encodeSpecificationId: sourceOrder.encodeSpecification.id,
                targetResolutionId: sourceOrder.targetResolution?.id,
                pONumber: sourceOrder.pONumber,
                soundtrackIds: sourceOrder.cloudAssets.soundtracks.map((s) => s.language.id),
                startDate: new Date(sourceOrder.startDate),
                dynamicSubtitlesIds: getUniqueDynamicSubtitles(sourceOrder.cloudAssets.subtitles).map((s) => s.id),
                embeddedSubtitlesId: sourceOrder.cloudAssets.video.subtitle?.id ?? CLEAN_VIDEO_SUBTITLE_LANGUAGE_ID,
                systemId: sourceOrder.system.id,
                targetFileName: sourceOrder.targetFileName,
                deliveryFolder: sourceOrder.deliveryInfo.deliveryFolder,
                subtitlesTargetFileName: sourceOrder?.subtitlesTargetFileName ?? "",
                isSubtitlesEmbedded: !isCleanVideo(sourceOrder.cloudAssets.video.subtitle?.id)
            });

            const selectedAirline = !!airlines?.length ? getById(airlines, sourceOrder.airline.id) : undefined;
            setAirline(selectedAirline);
            const selectedSystem = !!systems?.length ? getById(systems, sourceOrder.system.id) : undefined;
            setSystem(selectedSystem);

            setContentType(Number(sourceOrder.content.subtype));

            const { type } = sourceOrder.content;

            if (type === ContentReferenceType.Movie) {
                setMovieVersion(mapToMovieVersionItem(sourceOrder.content));
            } else if (type === ContentReferenceType.Ancillary) {
                setAncillaryVersion(mapToAncillaryVersionItem(sourceOrder.content));
            } else {
                setEpisodeVersion(mapToEpisodeVersionItem(sourceOrder.content));
            }

            setSoundtracks(
                sourceOrder.cloudAssets.soundtracks.map((soundtrack, idx) => ({
                    code: soundtrack.language.code,
                    id: soundtrack.language.id,
                    name: soundtrack.language.name,
                    index: idx + 1
                }))
            );

            if (sourceOrder.cloudAssets.video.subtitle) {
                setEmbeddedSubtitle({
                    code: sourceOrder.cloudAssets.video.subtitle.code,
                    id: sourceOrder.cloudAssets.video.subtitle.id,
                    name: sourceOrder.cloudAssets.video.subtitle.name
                });
            }

            setDynamicSubtitles(getUniqueDynamicSubtitles(sourceOrder.cloudAssets.subtitles));

            const encodeSpecification = !!encodeSpecifications?.length
                ? getById(encodeSpecifications, sourceOrder.encodeSpecification.id)
                : undefined;
            setEncodeSpecification(encodeSpecification);

            const targetResolutionId = sourceOrder.targetResolution?.id;
            if (targetResolutionId && resolutions) {
                const targetRes = resolutions.find((res) => res.id === targetResolutionId);
                ensure(targetRes, `Target Resolution ${targetRes} missing`);

                setTargetResolution(targetRes);
            }

            setTargetFileName(sourceOrder.targetFileName);
        }
    }, [sourceOrder, airlines, systems, encodeSpecifications]);

    useEffect(() => {
        if (!sourceOrder) {
            setContentDetails(undefined);

            return;
        }

        const id = getContentId(sourceOrder);
        const title = getContentTitle(sourceOrder);

        suggestContentListByTitle(title).then((response) => {
            const foundItem = response.items.find((item) => item.id === id);

            filterActiveContent(foundItem, sourceOrder.content.version.id);
            setContentDetails(foundItem);
        });
    }, [sourceOrder]);

    return { sourceOrder };
};

const filterActiveContent = (item: Optional<SuggestContentListByTitleResponseItem>, versionId: string) => {
    switch (item?.type) {
        case ContentReferenceType.Movie:
            item.versions = item.versions.filter((v) => !v.buzzVersion?.isArchived || v.id === versionId);

            break;
        case ContentReferenceType.TvSeries: {
            item.seasons.forEach((season) =>
                season.episodes.forEach(
                    (episode) =>
                        (episode.versions = episode.versions.filter(
                            (version) => !version.buzzVersion?.isArchived || version.id === versionId
                        ))
                )
            );
            break;
        }
    }
};

const isCleanVideo = (subtitleId?: string) => subtitleId === CLEAN_VIDEO_SUBTITLE_LANGUAGE_ID;

const getContentId = (order: GetOrderDetailsResponseItem): string => {
    if (order.content.type === ContentReferenceType.Movie) {
        return order.content.movie.id;
    }

    if (order.content.type === ContentReferenceType.TvSeries) {
        return order.content.tvSeries.id;
    }

    if (order.content.type === ContentReferenceType.Ancillary) {
        return order.content.ancillary.id;
    }

    throw new Error("Unsupported content type");
};

const getContentTitle = (order: GetOrderDetailsResponseItem): string => {
    if (order.content.type === ContentReferenceType.Movie) {
        return order.content.movie.title;
    }

    if (order.content.type === ContentReferenceType.TvSeries) {
        return order.content.tvSeries.title;
    }

    if (order.content.type === ContentReferenceType.Ancillary) {
        return order.content.ancillary.title;
    }

    throw new Error("Unsupported content type");
};

const getContentSeasonId = (order: GetOrderDetailsResponseItem): Optional<string> => {
    if (order.content.type !== ContentReferenceType.TvSeries) {
        return;
    }

    return order.content.season.id;
};

const getContentEpisodeId = (order: GetOrderDetailsResponseItem): Optional<string> => {
    if (order.content.type !== ContentReferenceType.TvSeries) {
        return;
    }

    return order.content.episode.id;
};

const getUniqueDynamicSubtitles = (subtitles: CloudOrderSubtitleResponse[]): CodedProperty[] => {
    const result: CodedProperty[] = [];

    for (const subtitle of subtitles) {
        if (!result.find((s) => s.id === subtitle.language.id)) {
            result.push({
                id: subtitle.language.id,
                code: subtitle.language.code,
                name: subtitle.language.name
            });
        }
    }

    return result;
};

const mapToMovieVersionItem = (content: OrderContentMovieReference): OrderMovieVersionItem => {
    return {
        type: content.type,
        movie: {
            id: content.movie.id,
            title: content.movie.title,
            buzzId: content.movie.buzzId
        },
        subtype: content.subtype,
        version: {
            id: content.version.id,
            buzzId: content.version.buzzId,
            type: content.version.type,
            distributor: content.version.distributor,
            brandings: content.version.brandings
        }
    };
};

const mapToAncillaryVersionItem = (content: OrderContentAncillaryReference): OrderAncillaryVersionItem => {
    return {
        type: content.type,
        ancillary: {
            id: content.ancillary.id,
            title: content.ancillary.title,
            buzzId: content.ancillary.buzzId
        },
        subtype: content.subtype,
        version: {
            id: content.version.id,
            buzzId: content.version.buzzId,
            type: content.version.type,
            airlines: content.version.airlines,
            distributor: content.version.distributor
        }
    };
};

const mapToEpisodeVersionItem = (content: OrderContentTvSeriesReference): OrderEpisodeVersionItem => {
    return {
        type: content.type,
        episode: {
            id: content.episode.id,
            title: content.episode.title,
            number: content.episode.number,
            buzzId: content.episode.buzzId
        },
        season: {
            id: content.season.id,
            name: content.season.name,
            buzzId: content.season.buzzId
        },
        tvSeries: {
            id: content.tvSeries.id,
            title: content.tvSeries.title,
            buzzId: content.tvSeries.buzzId
        },
        subtype: content.subtype,
        version: {
            id: content.version.id,
            buzzId: content.version.buzzId,
            type: content.version.type,
            distributor: content.version.distributor,
            brandings: content.version.brandings
        }
    };
};
